// ============================================================================
// AdminAgreementsPanel — legal agreements (Build S3, Task #19).
// ============================================================================
// Sidebar: #admin/agreements. LIST + DETAIL. AMP_PARTICIPATION only until S9
// (the API enforces; the UI doesn't offer other types). Detail: lifecycle,
// wired envelope, exhibits (publish new version), amendments.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, FileSignature, ArrowLeft, Plus } from 'lucide-react';
import {
  api, type AdminAgreement, type AdminAgreementDetail, type AdminFarm,
} from '../api';

interface Props {
  token: string;
  agreementId?: string;
}

const STATUS_TONES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  out_for_signature: 'bg-blue-100 text-blue-800',
  executed: 'bg-green-100 text-green-800',
  amended: 'bg-green-100 text-green-800',
  terminated: 'bg-red-100 text-red-700',
  expired: 'bg-gray-200 text-gray-600',
};

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_TONES[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function AdminAgreementsPanel({ token, agreementId }: Props) {
  if (agreementId !== undefined) return <AgreementDetail token={token} agreementId={agreementId} />;
  return <AgreementList token={token} />;
}

function AgreementList({ token }: { token: string }) {
  const [agreements, setAgreements] = useState<AdminAgreement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try { setAgreements((await api.adminAgreements(token)).agreements); }
    catch (e: any) { setError(e?.message || 'Could not load agreements'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-uif-green-dark flex items-center gap-2">
            <FileSignature className="h-5 w-5" /> Agreements
          </h1>
          <p className="text-sm text-gray-500">AMP Producer Participation Agreements — lifecycle + execution.</p>
        </div>
        <button type="button" onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark">
          <Plus className="h-4 w-4" /> New agreement
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-uif-green" /></div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2">Farm</th>
                <th className="px-3 py-2">Template</th>
                <th className="px-3 py-2">Effective</th>
                <th className="px-3 py-2">Amendments</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agreements.map((a) => (
                <tr key={a.agreement_id} className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => { window.location.hash = `#admin/agreements/${a.agreement_id}`; }}>
                  <td className="px-3 py-2 font-medium text-gray-900">{a.farm_code} — {a.farm_name}</td>
                  <td className="px-3 py-2 text-gray-600">{a.template_version}</td>
                  <td className="px-3 py-2 text-gray-600">{a.effective_date ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{a.amendment_count}</td>
                  <td className="px-3 py-2"><StatusPill status={a.status} /></td>
                </tr>
              ))}
              {agreements.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">No agreements yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <CreateAgreementModal token={token} onClose={() => setCreateOpen(false)}
          onCreated={(id) => { window.location.hash = `#admin/agreements/${id}`; }} />
      )}
    </div>
  );
}

function CreateAgreementModal({ token, onClose, onCreated }: {
  token: string; onClose: () => void; onCreated: (id: string) => void;
}) {
  const [farms, setFarms] = useState<AdminFarm[]>([]);
  const [farmId, setFarmId] = useState<number | ''>('');
  const [templateVersion, setTemplateVersion] = useState('AMP_v1_2026');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.adminFarms(token, { include_test: true }).then((r) => setFarms(r.farms)).catch(() => undefined);
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (farmId === '') return;
    setBusy(true); setError('');
    try {
      const r = await api.adminAgreementCreate(token, {
        agreement_type: 'AMP_PARTICIPATION', farm_id: Number(farmId),
        template_version: templateVersion.trim(),
      });
      onCreated(r.agreement_id);
    } catch (err: any) { setError(err?.message || 'Could not create agreement'); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3">
        <h2 className="text-lg font-bold text-uif-green-dark">New AMP participation agreement</h2>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Farm *</span>
          <select required value={farmId} onChange={(e) => setFarmId(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
            <option value="">Select…</option>
            {farms.map((f) => <option key={f.farm_id} value={f.farm_id}>{f.farm_code} — {f.farm_name}{f.is_test ? ' [TEST]' : ''}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Template version *</span>
          <input required value={templateVersion} onChange={(e) => setTemplateVersion(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </label>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={busy || farmId === ''}
            className="rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm disabled:opacity-40">
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </form>
    </div>
  );
}

function AgreementDetail({ token, agreementId }: { token: string; agreementId: string }) {
  const [detail, setDetail] = useState<AdminAgreementDetail | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError('');
    try { setDetail(await api.adminAgreementDetail(token, agreementId)); }
    catch (e: any) { setError(e?.message || 'Could not load agreement'); }
  }, [token, agreementId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // F-1 remediation: a pre-execution terminate action. Terminated agreements
  // leave the one-active-AMP-per-farm slot (the partial unique excludes
  // terminated/expired), unblocking a fresh agreement for the same farm.
  async function terminate() {
    if (!window.confirm('Terminate this agreement? This frees the farm\'s active-agreement slot and cannot be undone.')) return;
    setBusy(true); setError('');
    try { await api.adminAgreementPatch(token, agreementId, { status: 'terminated' }); await refresh(); }
    catch (e: any) { setError(e?.message || 'Could not terminate agreement'); }
    finally { setBusy(false); }
  }

  if (!detail) {
    return error
      ? <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
      : <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-uif-green" /></div>;
  }
  const a = detail.agreement;

  return (
    <div className="space-y-4">
      <a href="#admin/agreements" className="inline-flex items-center gap-1 text-sm text-uif-green-dark hover:underline">
        <ArrowLeft className="h-4 w-4" /> All agreements
      </a>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-uif-green-dark">
            {a.farm_code} — AMP Participation Agreement
          </h1>
          <div className="text-sm text-gray-500 flex items-center gap-2 flex-wrap">
            <StatusPill status={a.status} />
            <span>Template {a.template_version}</span>
            {a.effective_date && <span>· effective {a.effective_date}</span>}
            {a.envelope_id && (
              <a href={`#admin/envelopes/${a.envelope_id}`} className="text-uif-green-dark hover:underline">
                · view envelope{a.envelope_status ? ` (${a.envelope_status})` : ''}
              </a>
            )}
          </div>
        </div>
        {['draft', 'out_for_signature'].includes(a.status) && (
          <button type="button" disabled={busy} onClick={() => void terminate()}
            className="rounded-md border border-gray-300 text-gray-700 px-3 py-2 text-sm hover:border-red-400 hover:text-red-600 disabled:opacity-40">
            Terminate agreement
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2"><AlertCircle className="h-4 w-4 mt-0.5" />{error}</div>}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="font-semibold text-gray-900 mb-2">Exhibits (current versions)</h2>
        {detail.exhibits.filter((x) => x.is_current).length === 0 ? (
          <p className="text-sm text-gray-400">No exhibits published for this agreement.</p>
        ) : (
          <ul className="text-sm space-y-1">
            {detail.exhibits.filter((x) => x.is_current).map((x) => (
              <li key={x.exhibit_id} className="flex items-center gap-2">
                <span className="font-medium">Exhibit {x.exhibit_code}</span>
                <span className="text-gray-500">v{x.exhibit_version} · effective {x.effective_from}</span>
                <a href={`#admin/documents/${x.document_id}`} className="text-uif-green-dark hover:underline text-xs">view document</a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="font-semibold text-gray-900 mb-2">Amendments</h2>
        {detail.amendments.length === 0 ? (
          <p className="text-sm text-gray-400">
            No amendments. (Amend from an executed agreement: upload the amendment PDF as a document,
            then use the envelope flow — the amendment record materializes when all parties sign.)
          </p>
        ) : (
          <ul className="text-sm space-y-1">
            {detail.amendments.map((m) => (
              <li key={m.amendment_id}>
                <span className="font-medium">#{m.amendment_no}</span> {m.summary}
                <span className="text-gray-500"> · effective {m.effective_date}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {a.governing_notes && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h2 className="font-semibold text-gray-900 mb-1">Governing notes</h2>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{a.governing_notes}</p>
        </div>
      )}
    </div>
  );
}
